import {
  APP_URL_MAP,
  USER_CHANNEL_CS,
  USER_CHANNEL_DM,
  USER_CHANNEL_GA,
  USER_CHANNEL_LP,
  TRUE_VALUE_LIST,
  FALSE_VALUE_LIST,
} from '@common/constants';
import { isEmptyObject, isEmptyString, isNil } from '.';

/**
 * 랜덤한 ID 추출하는 함수
 * @param {object?} options 랜덤 ID 추출 시의 Options
 * @return {string} 랜덤한 ID
 * @author Robin
 */
export const getRandomId = (options = {}) => {
  const { prefix = '', length = 10 } = options;

  // RandomId의 첫 글자가 숫자인 경우, HTMLElement의 ID에서 오류가 나므로,
  // 첫글자에 r 글자 추가
  let randomId = `${prefix}r`;

  // length만큼 재생성
  while (randomId.length < length) {
    randomId = `${randomId}${Math.random()
      .toString(36)
      .substring(2)}`.substring(0, length);
  }

  return randomId;
};

/**
 * API를 통하여 외부 링크 (Curex, GAlips 등)의 token을 가져오는 함수
 * @param {function} 외부 링크 토큰 받아오는 함수
 * @returns {string} token 외부 링크 Token
 * @author Robin
 */
export const getLinkToken = async fetchLinkToken => {
  try {
    const response = await fetchLinkToken();

    const isTokenSuccess = $utils.checkApiSuccess(response);

    if (isTokenSuccess) {
      const { data } = response;

      const token = data?.RSLT_DATA;
      return token;
    } else {
      throw new Error('토큰 API 통신에 실패하였습니다.');
    }
  } catch (error) {
    console.log('getLinkToken() - error', error);
  }
};

/**
 * URL 주소의 유효성을 확인하여, 유효한 URL로 만드는 함수
 * @param {string} url 유효한지 확인할 URL
 * @return {string} 유효한 URL
 * @author Robin
 */
export const getSafeUrl = url => {
  const isProtocol = url.includes('http://') || url.includes('https://');
  return isProtocol ? url : `http://${url}`;
};

/**
 * 특정 URL에서 Query String 전체를 객체 형태로 리턴하는 함수
 * @param {string?} url Query String을 Parsing할 URL (Default: 현재 페이지 URL)
 * @return {object} Query String 내용의 객체
 * @author Robin
 */
export const getQueries = (url = window.location.href) => {
  const isQuery = url.includes('?');
  if (!isQuery) return {};

  const query = url.slice(url.indexOf('?') + 1);
  const queries = query.split('&');
  const queryObject = queries.reduce((acc, queryString) => {
    const [key, value] = queryString.split('=');
    acc[key] = value;
    return acc;
  }, {});

  return queryObject;
};

/**
 * URL에 queryInfo에 있는 데이터를 Query String으로 추가하는 함수
 * @param {string} url URL 주소
 * @param {object} queryInfo URL 주소에 추가할 query의 정보
 * @return {string} URL에 query 들이 추가된 새로운 URL
 * @author Robin
 */
export const addUrlQueries = (url = '', queryInfo = {}) => {
  const rawUrl = clearQueries(url);
  const urlQueries = getQueries(url);
  const newQueries = { ...urlQueries, ...queryInfo };
  const queryString = Object.keys(newQueries).reduce((queries, key, index) => {
    const value = newQueries[key];
    const connectLetter = index === 0 ? '?' : '&';
    return `${queries}${connectLetter}${key}=${value}`;
  }, '');
  const urlWithQueries = `${rawUrl}${queryString}`;

  return urlWithQueries;
};

/**
 * URL의 Query String을 제거해서 리턴하는 함수
 * @param {string} url Query String을 제거할 URL
 * @return {string} Query String이 제거된 Raw URL
 * @author Robin
 */
export const clearQueries = (url = '') => {
  return url?.split('?')[0];
};

/**
 * URL에 Gateway Host를 추가하는 함수
 * @param {string} url URL 주소
 * @return {string} Gateway Host가 추가된 URL
 * @author Robin
 */
export const addGatewayHost = (url = '') => {
  // URL 주소에 이미 Host가 포함 된 경우 리턴
  const isProtocol = url.includes('http://') || url.includes('https://');
  if (isProtocol) return url;

  // 기본적으로는 현재 URL Host
  let host = window.location.origin;

  // Local인 경우에 DEV로 변경
  if ($utils.envType === 'local') {
    const devProtocol = 'https://';
    host = `${devProtocol}${APP_URL_MAP.tablet.dev}`;
  }

  // Host 붙인 최종 URL
  const resultUrl = `${host}${url}`;

  return resultUrl;
};

/**
 * 특정 URL에서 특정 Query 들을 제거 하는 함수
 * @param {string} url 특정 Query String을 제거할 URL
 * @param {Array} keys URL에서 제거 될 Query String의 목록 배열
 * @return {string} 특정 keys들의 Query 들이 제거 된 URL
 * @author Robin
 */
export const removeQueries = (url, keys = []) => {
  const queries = getQueries(url);
  const queryObject = keys.reduce((acc, removingKey) => {
    delete acc[removingKey];
    return acc;
  }, queries);

  const urlWithoutQuery = url.slice(0, url.indexOf('?'));
  const newUrl = addUrlQueries(urlWithoutQuery, queryObject);

  return newUrl;
};

/**
 * 링크 메뉴의 URL에 토큰이 필요하면 토큰을 붙여서 접속가능한 URL로 만드는 함수
 * @param {string} menuUrl 설계사 메뉴목록 API에서 받은 해당 링크 메뉴의 URL Text
 * @param {string} token 링크 토큰 API를 호출하여 받은 Link Token
 * @return {string} 링크 토큰이 붙여진 URL
 */
export const makeLinkMenuUrl = (menuUrl, token) => {
  // 로그인 설계사 메뉴목록 API에서 받은 링크 메뉴는 내/외부 주소가(,)로 구분되어 있어
  // 현재 접속된 내/외부 여부에 따라 필요한 URL을 꺼내서 사용
  const links = menuUrl.split(',');
  const isOneUrl = links.length === 1;

  let linkUrl;
  if (isOneUrl) linkUrl = links[0];
  else linkUrl = !$utils.checkExternal() ? links[0] : links[1];

  const safeLinkUrl = getSafeUrl(linkUrl);

  if (token) {
    // URL 에 token=이 포함되어 있으면 제거 후 다시 추가
    // - URL에 token=이 있는 경우와 없는 경우 둘 다 가능하도록 하기 위함
    const cleanedLinkUrl = removeQueries(safeLinkUrl, ['token']);
    const linkUrlWithToken = addUrlQueries(cleanedLinkUrl, { token });
    return linkUrlWithToken;
  } else return safeLinkUrl;
};

/**
 * Track2 직업 검색 API에서 받은 내용을 Asis 직업 검색 데이터 형태로 변경시켜주는 함수
 * @param {object} jobData Track2 직업 검색에서 받은 response Data
 * @return {object} Asis 형태의 직업 검색 response Data
 * @author Robin
 */
export const convertJobData = jobData => {
  const convertedJobData = jobData.map(jobItem => {
    const { jobCd, jobNm } = jobItem;
    return {
      ...jobItem,
      cdVal1: jobCd,
      cdDsc: jobNm,
      rplceCdVal03: jobCd,
    };
  });

  return convertedJobData;
};

/**
 * 서버에서 받은 잘못된 데이터들을 clean 하는 함수
 * @param {object} serverDataObject 서버에서 받은 Data Object
 * @param {string[]?} exceptions 변경하지 않을 예외 Data Key
 * @return {object} 잘못된 값들이 정리된 Data Object
 * @author Robint
 */
export const cleanServerObject = (serverDataObject, exceptions = []) => {
  const cleanedObject = Object.keys(serverDataObject).reduce((obj, key) => {
    const value = serverDataObject[key];
    const isException = exceptions.includes(key);

    if (isException) obj[key] = value;
    else {
      obj[key] =
        value === 'null' || value === 'undefined'
          ? ''
          : !isNil(value) && typeof value === 'object'
          ? cleanServerObject(value)
          : value;
    }

    return obj;
  }, {});

  return cleanedObject;
};

/**
 * 값이 없는 경우 기본 값으로 데이터를 세팅하는 함수
 * @param {object} obj 데이터가 있는 대상 객체
 * @param {string} key 데이터의 key
 * @param {any} defaultValue 데이터가 없을 경우의 기본 값
 * @return {void}
 * @author Robin
 */
export const setDefaultValue = (obj, key, defaultValue) => {
  obj[key] = isEmptyString(obj[key]) ? defaultValue : obj[key];
};

/**
 * 공통 코드에서 불러온 값을 Select 컴포넌트에서 사용할 Data 형태로 변경해주는 함수
 * @param {object[]} 서버에서 받아온 공통 코드 데이터 형태의 Array
 * @return {object[]} Select 컴포넌트에서 사용할 Data 형태의 Array
 * @author Robin
 */
export const makeCodeOptions = codeInfos => {
  return codeInfos.map(({ cdVal, cmnCdNm, cdContn }) => ({
    label: cmnCdNm ? cmnCdNm : cdContn,
    value: cdVal,
  }));
};

/**
 * <SatOption>의 options Prop의 label이 UI에 노출 및 데이터로 입력되도록 변경하는 함수
 * @param {object[]} options <SatOption>의 options Prop
 * @return {object[]} label, value 둘 다 label인 <SatOption>의 options Prop
 * @author Robin
 */
export const convertLabelOptions = options => {
  const labelOptions = options.map(option => ({
    ...option,
    // option.label이 label, value 둘 다 되도록 변경
    value: option.label,
  }));
  return labelOptions;
};

/**
 * 해당 채널 코드가 LP 채널인지 판별하는 함수
 * @param {string} channelCode 채널 코드
 * @returns {boolean} LP 채널 여부
 * @author Robin
 */
export const checkLP = channelCode => {
  return channelCode === USER_CHANNEL_LP;
};

/**
 * 해당 채널 이름이 LP 채널 Text인지 판별하는 함수
 * @param {string} channelName 채널 이름
 * @return {boolean} LP 채널 여부
 * @author Robin
 */
export const checkLPText = channelName => {
  return ['LP', 'KBLP'].includes(channelName?.trim());
};

/**
 * 해당 채널 코드가 GA 채널인지 판별하는 함수
 * @param {string} channelCode 채널 코드
 * @return {boolean} GA 채널 여부
 * @author Robin
 */
export const checkGA = channelCode => {
  return channelCode === USER_CHANNEL_GA;
};

/**
 * 해당 채널 코드가 DM 채널인지 판별하는 함수
 * @param {string} channelCode 채널 코드
 * @return {boolean} DM 채널 여부
 * @author Robin
 */
export const checkDM = channelCode => {
  return channelCode === USER_CHANNEL_DM;
};

/**
 * 해당 채널 코드가 CS 채널인지 판별하는 함수
 * @param {string} channelCode 채널 코드
 * @return {boolean} CS 채널 여부
 * @author Robin
 */
export const checkCS = channelCode => {
  return channelCode === USER_CHANNEL_CS;
};

/**
 * 각 채널의 true/false 여부 정보를 return 하는 함수
 * @param {string} channelCode 채널 코드
 * @return {object} 각 채널의 여부 true/false
 * @author Robin
 */
export const getChannelCheckInfo = channelCode => {
  return {
    isLP: checkLP(channelCode),
    isGA: checkGA(channelCode),
    isDM: checkDM(channelCode),
    isCS: checkCS(channelCode),
  };
};

/**
 * Multi Channel List API 에서 받아온 채널의 중복을 제거하고 선택 가능한 채널들만 리턴하는 함수
 * @param {object[]} userChannels Multi Channel List API에서 받아온 유저의 채널 리스트
 * @return {object[]} 선택 가능한 채널 리스트
 * @author Robin
 */
export const filterAvailableChannels = userChannels => {
  const availableChannels = userChannels.filter(({ cdVal }, channelIndex) => {
    const firstOccurenceIndex = userChannels.findIndex(
      channelInfo => channelInfo.cdVal === cdVal,
    );
    return channelIndex === firstOccurenceIndex;
  });

  return availableChannels;
};

/**
 * 유저의 채널 정보가 선택 가능 채널 리스트(avaliableChannels)의 값 비교
 * [서버에서 내려온 채널 리스트가 1개이면서, 유저의 토큰에 세팅 되어있는 채널이 같은지 확인]
 * @param {object[]} availableChannels 선택가능한 채널 리스트
 * @param {object} tokenInfo 토큰 정보
 * @return {boolean} 토큰정보에 있는 유저의 채널과 선택가능한 토큰이 한 개이면서 그 채널이 유저 기본 채널과 같은 지 여부
 * @author Robin
 */
export const checkUserSameChannel = (availableChannels, tokenInfo) => {
  let userChannelCode = tokenInfo?.orgChnlCd;

  // 선택 가능한 채널이 2개 이상이라면 채널 재선택 필요
  if (availableChannels.length >= 2) return false;
  else {
    const channelInfo = availableChannels[0];
    const isUserSameChannel = channelInfo?.cdVal === userChannelCode;
    return isUserSameChannel;
  }
};

/**
 * 시스템헬프데스크 권한 보유 여부를 return 하는 함수
 * @param {object} tokenInfo 토큰 정보
 * @return {boolean} 시스템헬프데스크 권한을 가지고 있는지 여부
 */
export const isHelpDesk = (tokenInfo) => {
  if (tokenInfo.roleIds.length > 0) {
    for (let i = 0; i < tokenInfo.roleIds.length; i++) {
      if (tokenInfo.roleIds[i] === 'LOS_HQ_SYS_HELPDESK') {
        return true;
      }
    }
  }
  return false;
};

/**
 * 생애종합분석 조회 권한 보유 여부를 return 하는 함수 (DM 특정 ROLE 과 헬프데스크)
 * @param {object} tokenInfo 토큰 정보
 * @return {boolean} 생애종합분석 조회 권한을 가지고 있는지 여부
 */
export const roleForPfmsPage = (tokenInfo) => {
  const authRole = [
    'LOS_HQ_SYS_HELPDESK', //헬프데스크
    'LOS_DM_EWS', //DM
    'LOS_DM_GE_AFFAIRS',
    'LOS_DM_PLANER',
    'LOS_DM_PRVCY_AGENCY',
    'LOS_DM_SALES_MNGR'
  ];
  if (tokenInfo.roleIds.length > 0) {
    for (let i = 0; i < tokenInfo.roleIds.length; i++) {
      if (authRole.includes(tokenInfo.roleIds[i])) {
        return true;
      }
    }
  }
  return false;
};

/**
 * 메시지 String 배열을 구분자[기본값:줄바꿈]를 붙여 하나의 String으로 리턴하는 함수
 * @param {string[]} messages 메시지 String 배열
 * @return {string} 구분자를 더해 합쳐진 메시지 String
 * @author Robin
 */
export const concatMessages = (messages = [], separator = '\n') => {
  return messages.join(separator);
};

/**
 * Array에서 특정 index에 값을 삽입하여 새로운 Array로 리턴하는 함수
 * @param {Array} arr 원본 Array
 * @param {number} index 추가할 index
 * @param {any} value 추가할 value
 * @return {Array} 해당 index에 value가 추가 된 새로운 Array
 * @author Robin
 */
export const putItem = (arr = [], index, value) => {
  const newArr = [...arr];
  newArr.splice(index, 0, value);
  return newArr;
};

/**
 * Object의 데이터를 다른 Object의 데이터로 업데이트 하는 함수
 * @param {object} obj 업데이트 할 대상 Object
 * @param {object} dataObj 업데이트 데이터가 있는 Object
 * @return {object} dataObj의 데이터가 반영된 obj
 * @author Robin
 */
export const updateObject = (obj, dataObj) => {
  const updatedObj = Object.keys(obj).reduce((newObj, key) => {
    newObj[key] = dataObj[key] ? dataObj[key] : obj[key];
    return newObj;
  }, {});

  return updatedObj;
};

/**
 * Object Array에서 특정 key가 특정 value 값 인 것들을 제거하는 함수
 * @param {object[]} list 전체 정보 Object Array
 * @param {string} key 제외할 value가 존재하는 Key 값
 * @param {string[]} values 제외할 value 들의 Array
 * @return {object[]} 특정 value 들이 제외된 새로운 Object Array
 * @author Robin
 */
export const filterObjects = (list = [], key = 'value', values = []) => {
  return list.filter(item => !values.includes(item[key]));
};

/**
 * Object Array에서 특정 Key를 기준으로 정렬하는 함수
 * @param {object[]} list 전체 정보 Object Array
 * @param {string} key 제외할 value가 존재하는 Key 값
 * @param {object?} options 정렬 옵션들
 * @return {object[]} 특정 value 들이 제외된 새로운 Object Array
 * @author Robin
 */
export const sortObjects = (list = [], key = 'value', options = {}) => {
  const { order = 'desc' } = options;

  return list.sort((a, b) =>
    order === 'desc' ? a[key] - b[key] : b[key] - a[key],
  );
};

/**
 * Object에서, 값이 있는 항목들만 모아서 리턴해주는 함수
 * @param {object} obj 값이 있는 key만 리턴할 대상 Object
 * @return {object} 값이 있는 key 로만 되어있는 Object
 * @author Robin
 */
export const cleanObject = (obj = {}, options = {}) => {
  const { isRemoveEmpty = false } = options;

  const cleanObj = Object.keys(obj).reduce((newObj, key) => {
    const value = obj[key];
    const isNotNil = !isNil(value);
    const isValid = isRemoveEmpty
      ? (typeof value === 'string' && !isEmptyString(value)) ||
        (typeof value === 'object' && !isEmptyObject(value))
      : isNotNil;

    if (isValid) newObj[key] = value;
    return newObj;
  }, {});

  return cleanObj;
};

/**
 * 특정 Selector Element가 해당 Element 자식으로 존재하는지 확인하는 함수
 * @param {string} element 부모 Element
 * @param {string} selector 자식을 검색할 Selector
 * @return {boolean}
 * @author Robin
 */
export const checkChildExist = (element, selector) => {
  const isElementExist = element.querySelector(selector);
  return isElementExist;
};

/**
 * HTML Element 를 캡쳐하는 함수
 * @param {HTMLElement} element 캡쳐할 HTML Element
 * @param {string[]} exceptionClassNames 캡쳐에 제외할 클래스명
 * @return {string} 캡쳐된 이미지의 base64 값
 * @author Robin
 */
export const captureElement = async (element, exceptionClassNames = []) => {
  const imageData = await htmlToImage.toPng(element, {
    filter: node => {
      const isExceptionElement = exceptionClassNames.some(className =>
        node.className?.includes(className),
      );

      return !isExceptionElement;
    },
  });

  return imageData;
};

export const isTrue = value => TRUE_VALUE_LIST.includes(value);
export const isFalse = value => FALSE_VALUE_LIST.includes(value);

/**
 * 신계약 참/거짓 값으로 변환 후 반환하는 함수
 * @param {*} value
 * @returns
 */
export const convertTrueForNbs = value => {
  if (isTrue(value)) {
    return '1';
  }
  return '0';
};
